/* ==========================================================================
   TressAllure (TA) · Release theme — Product Video (zoom)
   Custom element. Types: 'vimeo' (SDK, auto-advance), 'embed' (YouTube
   iframe), 'file' (direct <video>).

   Modes:
   - Docked sticky (class pvg-modal--right, viewport >= 750px): fixed panel,
     right side, vertically centered. Page scrolls freely; panel stays put.
     No scroll lock, no backdrop. Closes on X, Escape, or click outside.
   - Overlay (centered / any mobile): fixed + veil + scroll lock.

   Editor-safe lifecycle: the modal lives in <body> and is re-adopted (or
   rebuilt from the existing copy) when the theme editor re-renders the
   section, so the component survives Save / block moves.
   ========================================================================== */

(function () {
  'use strict';

  if (!window.customElements || customElements.get('product-video-gallery')) return;

  var VIMEO_SDK = 'https://player.vimeo.com/api/player.js';
  var DESKTOP_MQ = '(min-width: 750px)';
  var sdkPromise = null;

  function loadVimeoSdk() {
    if (window.Vimeo && window.Vimeo.Player) return Promise.resolve();
    if (sdkPromise) return sdkPromise;

    sdkPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = VIMEO_SDK;
      script.async = true;
      script.onload = resolve;
      script.onerror = function () {
        sdkPromise = null;
        reject(new Error('Vimeo SDK failed to load'));
      };
      document.head.appendChild(script);
    });

    return sdkPromise;
  }

  function buildVimeoUrl(id, hash, muted) {
    var url =
      'https://player.vimeo.com/video/' +
      encodeURIComponent(id) +
      '?autoplay=1&playsinline=1&title=0&byline=0&portrait=0';

    if (hash) url += '&h=' + encodeURIComponent(hash);
    if (muted) url += '&muted=1';

    return url;
  }

  /** Append autoplay params to a prebuilt embed URL (YouTube). */
  function buildGenericEmbedUrl(src, muted) {
    var sep = src.indexOf('?') > -1 ? '&' : '?';
    return (
      src + sep + 'autoplay=1&playsinline=1&rel=0&modestbranding=1&mute=' + (muted ? '1' : '0')
    );
  }

  function formatDuration(seconds) {
    var total = Math.round(Number(seconds) || 0);
    if (total <= 0) return '';

    var hours = Math.floor(total / 3600);
    var mins = Math.floor((total % 3600) / 60);
    var secs = total % 60;
    var pad = function (n) {
      return n < 10 ? '0' + n : String(n);
    };

    return hours > 0 ? hours + ':' + pad(mins) + ':' + pad(secs) : mins + ':' + pad(secs);
  }

  function fetchVimeoMeta(canonicalUrl) {
    var cacheKey = 'pvg:' + canonicalUrl;

    try {
      var cached = sessionStorage.getItem(cacheKey);
      if (cached) return Promise.resolve(JSON.parse(cached));
    } catch (e) {
      /* private mode / storage disabled */
    }

    var endpoint =
      'https://vimeo.com/api/oembed.json?url=' + encodeURIComponent(canonicalUrl) + '&width=960';

    return fetch(endpoint)
      .then(function (response) {
        if (!response.ok) throw new Error('oEmbed ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var meta = {
          thumbnail_url: data.thumbnail_url || '',
          duration: data.duration || 0,
          title: data.title || ''
        };
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(meta));
        } catch (e) {
          /* noop */
        }
        return meta;
      });
  }

  class ProductVideoGallery extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = -1;
      this.isOpen = false;
      this.lastFocused = null;
      this.vimeoPlayer = null;
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleOutsideClick = this.handleOutsideClick.bind(this);
    }

    connectedCallback() {
      if (this.pvgReady) return;

      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-pvg-card]'));
      if (!this.cards.length) return;

      // Modal resolution (editor-safe):
      // 1. Fresh server render -> the modal is inside this element.
      // 2. Re-connect after an editor re-render/move -> the modal already
      //    lives in <body>; rebuild a clean copy from it (clone drops any
      //    listeners bound by a previous instance).
      var modalId = this.dataset.modalId || '';
      var modal = this.querySelector('[data-pvg-modal]');

      if (!modal && modalId) {
        var existing = document.getElementById(modalId);
        if (existing) modal = existing.cloneNode(true);
      }
      if (!modal) return;

      // Purge every other copy with the same id, then (re)attach to <body>.
      var purgeId = modal.id || modalId;
      if (purgeId) {
        var stale = document.querySelectorAll('[id="' + purgeId + '"]');
        for (var s = 0; s < stale.length; s++) {
          if (stale[s] !== modal && stale[s].parentNode) {
            stale[s].parentNode.removeChild(stale[s]);
          }
        }
      }
      modal.hidden = true;
      document.body.appendChild(modal);

      this.pvgReady = true;
      this.startMuted = this.dataset.startMuted === 'true';
      this.showDuration = this.dataset.showDuration === 'true';
      this.wantsDock = modal.classList.contains('pvg-modal--right');

      this.modal = modal;
      this.videoEl = modal.querySelector('[data-pvg-video]');
      this.embedEl = modal.querySelector('[data-pvg-embed]');
      this.errorEl = modal.querySelector('[data-pvg-error]');
      this.titleEl = modal.querySelector('[data-pvg-title]');
      this.prevBtn = modal.querySelector('[data-pvg-prev]');
      this.nextBtn = modal.querySelector('[data-pvg-next]');

      if (this.cards.length < 2) modal.classList.add('is-single');

      var self = this;

      this.cards.forEach(function (card, index) {
        // Guard against double-binding when the same DOM re-connects.
        if (card.dataset.pvgBound) return;
        card.dataset.pvgBound = '1';
        card.addEventListener('click', function (event) {
          // Docked mode closes on outside click; don't let this click count.
          event.stopPropagation();
          self.open(index);
        });
      });

      Array.prototype.forEach.call(modal.querySelectorAll('[data-pvg-close]'), function (btn) {
        btn.addEventListener('click', function () {
          self.close();
        });
      });

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', function () {
          self.step(-1);
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', function () {
          self.step(1);
        });
      }

      if (this.videoEl) {
        this.videoEl.addEventListener('ended', function () {
          if (self.cards.length > 1) self.step(1);
        });
        // Source failed (404, expired link, unsupported format): say it.
        this.videoEl.addEventListener('error', function () {
          if (self.videoEl.hidden) return;
          self.showError('This video is temporarily unavailable.');
        });
      }

      // Idempotent: remove before add so re-connects never double-bind.
      document.removeEventListener('keydown', this.handleKeydown);
      document.addEventListener('keydown', this.handleKeydown);

      this.hydrateThumbnails();
    }

    disconnectedCallback() {
      // Do NOT remove the modal from <body>: the theme editor disconnects and
      // re-connects this element on Save / block moves, and the next
      // connectedCallback re-adopts the modal by id. Only stop playback and
      // release document-level listeners.
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('click', this.handleOutsideClick, true);
      this.stopPlayback();
      if (this.modal) this.modal.hidden = true;
      this.isOpen = false;
      document.body.classList.remove('pvg-no-scroll');
      this.pvgReady = false;
    }

    /** Docked mode is only for desktop; mobile always gets the overlay. */
    isDocked() {
      return this.wantsDock;
    }

    /* ------------------------------ Thumbnails --------------------------- */

    hydrateThumbnails() {
      if (this.dataset.fetchMeta !== 'true') {
        this.clearLoadingStates();
        return;
      }

      var self = this;
      var pending = this.cards.filter(function (card) {
        return !!card.dataset.oembed;
      });

      if (!pending.length) {
        this.clearLoadingStates();
        return;
      }

      pending.forEach(function (card) {
        fetchVimeoMeta(card.dataset.oembed)
          .then(function (meta) {
            self.applyMeta(card, meta);
          })
          .catch(function () {
            var wrap = card.querySelector('[data-pvg-thumb-wrap]');
            if (wrap) wrap.classList.remove('is-loading');
          });
      });
    }

    applyMeta(card, meta) {
      var wrap = card.querySelector('[data-pvg-thumb-wrap]');
      var img = card.querySelector('[data-pvg-thumb]');
      var durationEl = card.querySelector('[data-pvg-duration]');

      if (img && meta.thumbnail_url) {
        img.src = meta.thumbnail_url;
        img.hidden = false;
        if (!img.getAttribute('alt') && meta.title) img.setAttribute('alt', meta.title);
      }

      if (wrap) wrap.classList.remove('is-loading');

      if (this.showDuration && durationEl) {
        var label = formatDuration(meta.duration);
        if (label) {
          durationEl.textContent = label;
          durationEl.hidden = false;
        }
      }

      if (!card.dataset.title && meta.title) {
        card.dataset.title = meta.title;
        card.setAttribute('aria-label', 'Play video: ' + meta.title);
      }
    }

    clearLoadingStates() {
      this.cards.forEach(function (card) {
        var wrap = card.querySelector('[data-pvg-thumb-wrap]');
        if (wrap) wrap.classList.remove('is-loading');
      });
    }

    /* ------------------------------ Navigation --------------------------- */

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (this.cards.length < 2) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1);
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
      }
    }

    /** Docked mode: click anywhere outside the panel closes it. */
    handleOutsideClick(event) {
      if (!this.isOpen) return;
      if (this.modal.contains(event.target)) return;
      this.close();
    }

    step(delta) {
      var total = this.cards.length;
      var next = (this.currentIndex + delta + total) % total;
      this.load(next);
    }

    open(index) {
      if (!this.isOpen) {
        this.lastFocused = document.activeElement;
        this.modal.hidden = false;
        this.isOpen = true;

        if (this.isDocked()) {
          // Sticky/mini panel: page keeps scrolling; panel is fixed via CSS.
          // Outside-click close only on desktop; on mobile the mini player
          // stays open while browsing (wigs.com behavior) until the X.
          if (window.matchMedia(DESKTOP_MQ).matches) {
            document.addEventListener('click', this.handleOutsideClick, true);
          }
        } else {
          document.body.classList.add('pvg-no-scroll');
        }
      }

      this.load(index);

      var closeBtn = this.modal.querySelector('[data-pvg-close].pvg-modal__ctrl');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }

    load(index) {
      var card = this.cards[index];
      if (!card) return;

      this.currentIndex = index;
      if (this.titleEl) this.titleEl.textContent = card.dataset.title || '';

      this.stopPlayback();
      this.hideError();

      if (card.dataset.type === 'vimeo') {
        this.playVimeo(card);
      } else if (card.dataset.type === 'embed') {
        this.playEmbed(card);
      } else {
        this.playFile(card.dataset.src);
      }
    }

    /* ------------------------------ Players ------------------------------ */

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    playVimeo(card) {
      var self = this;

      this.videoEl.hidden = true;
      this.embedEl.hidden = false;

      var iframe = document.createElement('iframe');
      iframe.setAttribute('title', card.dataset.title || 'Video');
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute(
        'allow',
        'autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media'
      );
      iframe.setAttribute('allowfullscreen', '');
      iframe.src = buildVimeoUrl(card.dataset.vimeoId, card.dataset.vimeoHash, this.startMuted);

      this.embedEl.appendChild(iframe);

      loadVimeoSdk()
        .then(function () {
          if (!self.embedEl.contains(iframe)) return;

          var player = new window.Vimeo.Player(iframe);
          self.vimeoPlayer = player;

          player.on('ended', function () {
            if (self.cards.length > 1) self.step(1);
          });

          player.play().catch(function () {
            player
              .setMuted(true)
              .then(function () {
                return player.play();
              })
              .catch(function () {});
          });
        })
        .catch(function () {
          /* No SDK: the plain iframe still plays, without auto-advance. */
        });
    }

    playEmbed(card) {
      this.videoEl.hidden = true;
      this.embedEl.hidden = false;

      var iframe = document.createElement('iframe');
      iframe.setAttribute('title', card.dataset.title || 'Video');
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
      );
      iframe.setAttribute('allowfullscreen', '');
      iframe.src = buildGenericEmbedUrl(card.dataset.src, this.startMuted);

      this.embedEl.appendChild(iframe);
    }

    playFile(src) {
      var self = this;

      this.embedEl.hidden = true;
      this.videoEl.hidden = false;
      this.videoEl.muted = this.startMuted;
      this.videoEl.setAttribute('src', src);
      this.videoEl.load();

      var attempt = this.videoEl.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(function () {
          self.videoEl.muted = true;
          var retry = self.videoEl.play();
          if (retry && typeof retry.catch === 'function') retry.catch(function () {});
        });
      }
    }

    stopPlayback() {
      if (this.vimeoPlayer) {
        try {
          this.vimeoPlayer.destroy();
        } catch (e) {
          /* noop */
        }
        this.vimeoPlayer = null;
      }

      if (this.embedEl) {
        this.embedEl.innerHTML = '';
        this.embedEl.hidden = true;
      }

      if (this.videoEl) {
        try {
          this.videoEl.pause();
        } catch (e) {
          /* noop */
        }
        this.videoEl.removeAttribute('src');
        this.videoEl.load();
        this.videoEl.hidden = true;
      }
    }

    close() {
      if (!this.isOpen) return;

      this.stopPlayback();
      this.hideError();
      this.modal.hidden = true;
      this.isOpen = false;
      document.body.classList.remove('pvg-no-scroll');
      document.removeEventListener('click', this.handleOutsideClick, true);

      var target = this.cards[this.currentIndex] || this.lastFocused;
      if (target && typeof target.focus === 'function') {
        target.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('product-video-gallery', ProductVideoGallery);
})();